import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  DEFAULT_AUDIO_SETTINGS,
  getAudioSettings,
  setAudioSettings,
  subscribeAudioSettings,
} from '../utils/audioSettings';

const AudioSettingsContext = createContext(null);

export function AudioSettingsProvider({ username, children }) {
  const [settings, setSettingsState] = useState(() => getAudioSettings());
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    return subscribeAudioSettings(setSettingsState);
  }, []);

  useEffect(() => {
    if (!username) {
      setAudioSettings(DEFAULT_AUDIO_SETTINGS);
      setLoaded(true);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get('/api/audio-settings', { params: { username } });
        if (cancelled) return;
        if (res.data?.success && res.data.settings) {
          setAudioSettings(res.data.settings);
        } else {
          setAudioSettings(DEFAULT_AUDIO_SETTINGS);
        }
      } catch (_) {
        if (!cancelled) setAudioSettings(DEFAULT_AUDIO_SETTINGS);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [username]);

  const saveSettings = useCallback(async (next) => {
    const merged = { ...getAudioSettings(), ...next };
    setAudioSettings(merged);
    if (!username) return true;

    try {
      const res = await axios.post('/api/audio-settings', {
        username,
        settings: merged,
      });
      return Boolean(res.data?.success);
    } catch (_) {
      return false;
    }
  }, [username]);

  const value = useMemo(() => ({
    settings,
    loaded,
    saveSettings,
  }), [settings, loaded, saveSettings]);

  return (
    <AudioSettingsContext.Provider value={value}>
      {children}
    </AudioSettingsContext.Provider>
  );
}

export function useAudioSettings() {
  const ctx = useContext(AudioSettingsContext);
  if (!ctx) {
    throw new Error('useAudioSettings must be used within AudioSettingsProvider');
  }
  return ctx;
}

export function useAudioSettingsOptional() {
  return useContext(AudioSettingsContext);
}
